/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
} from "../../utils/apiConstants.js";
import { logger, sanitizeError } from "../../utils/logger.js";
/**
 * @fileoverview Appointment logic module.
 * Handles appointment scheduling, retrieval, and management for business events.
 * @module logic/appointment
 */

import { refreshToken, signOut } from "../../utils/common.js";

/**
 * Fetches all appointments for a user in a specific event.
 * @async
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @param {string} userId - The user's unique identifier
 * @param {string} eventId - The event's unique identifier
 * @returns {Promise<Array>} Array of appointment objects or empty array on error
 */
export const getUserAppointmentsForEvent = async (
  navigate,
  userId,
  eventId,
) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}appointment/event/${eventId}/user/${userId}/`,
      options,
    );

    const result = await response.json();

    logger.info("getUserAppointmentsForEvent fetch result:", { data: result });

    if (result?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getUserAppointmentsForEvent(navigate, userId, eventId);
    }

    return result.data;
  } catch (error) {
    logger.error("Error fetching user appointments for event:", {
      err: sanitizeError(error),
    });
    return [];
  }
};

/**
 * Fetches all appointments sent by a user for a specific event.
 * @async
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @param {string} senderUserId - The sender user's unique identifier
 * @param {string} eventId - The event's unique identifier
 * @returns {Promise<Array>} Array of sent appointment objects or empty array on error
 */
export const getUserSentAppointmentsForEvent = async (
  navigate,
  senderUserId,
  eventId,
) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}appointment/event/${eventId}/sender/${senderUserId}/`,
      options,
    );

    const result = await response.json();

    logger.info("getUserSentAppointmentsForEvent fetch result:", {
      data: result,
    });

    if (result?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getUserSentAppointmentsForEvent(
        navigate,
        senderUserId,
        eventId,
      );
    }

    return result.data;
  } catch (error) {
    logger.error("Error fetching user sent appointments for event:", {
      err: sanitizeError(error),
    });
    return [];
  }
};

/**
 * Creates a new appointment.
 * @async
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @param {Object} appointmentData - Appointment data to create
 * @returns {Promise<Object|undefined>} Created appointment data or undefined on error
 */
export const createAppointment = async (navigate, appointmentData) => {
  const options = {
    method: "POST",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(appointmentData),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}appointment`,
      options,
    );
    const result = await response.json();

    logger.info("createAppointment fetch result:", { data: result });

    if (result?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return;
      }
      return await createAppointment(navigate, appointmentData);
    }

    return result;
  } catch (error) {
    logger.error("Error updating appointment:", { err: sanitizeError(error) });
    return;
  }
};

/**
 * Updates an existing appointment.
 * @async
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @param {string} appointmentId - The appointment's unique identifier
 * @param {string} eventId - The event's unique identifier
 * @param {Object} appointmentData - Updated appointment data
 * @returns {Promise<Object|undefined>} Updated appointment data or undefined on error
 */
export const updateAppointment = async (
  navigate,
  appointmentId,
  eventId,
  appointmentData,
) => {
  const options = {
    method: "PATCH",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(appointmentData),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}appointment/${eventId}/${appointmentId}`,
      options,
    );
    const result = await response.json();

    logger.info("updateAppointment fetch result:", { data: result });

    if (result?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return;
      }
      return await updateAppointment(
        navigate,
        appointmentId,
        eventId,
        appointmentData,
      );
    }

    return result.data;
  } catch (error) {
    logger.error("Error updating appointment:", { err: sanitizeError(error) });
    return;
  }
};

/**
 * Fetches a single appointment by its identifier.
 * @async
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @param {string} appointmentId - The appointment's unique identifier
 * @param {string} eventId - The event's unique identifier
 * @returns {Promise<Object|undefined>} Appointment data or undefined on error
 */
export const getAppointment = async (navigate, appointmentId, eventId) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}appointment/${eventId}/${appointmentId}`,
      options,
    );
    const result = await response.json();

    logger.info("getAppointment fetch result:", { data: result });

    if (result?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return;
      }
      return await getAppointment(navigate, appointmentId, eventId);
    }

    return result.data;
  } catch (error) {
    logger.error("Error getting appointment:", { err: sanitizeError(error) });
    return;
  }
};

/**
 * Fetches the full appointment detail used by see request, see profile and make virtual.
 * @async
 * @returns {Promise<{ appointment: Object, requirement: Object|null, senderUser: Object|null, participatingCompany: Object|null, senderCompany: Object|null }|null>}
 */
export const getAppointmentDetail = async (
  navigate,
  appointmentId,
  eventId,
) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}appointment/${eventId}/${appointmentId}/detail`,
      options,
    );
    const result = await response.json();

    if (result?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await getAppointmentDetail(navigate, appointmentId, eventId);
    }

    if (!response.ok) {
      return null;
    }

    return result.data || null;
  } catch (error) {
    logger.error("Error getting appointment detail:", {
      err: sanitizeError(error),
    });
    return null;
  }
};

const buildAppointmentQueryString = (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }
    searchParams.set(key, String(value));
  });
  const query = searchParams.toString();
  return query ? `?${query}` : "";
};

const emptyAppointmentsPage = {
  items: [],
  page: 1,
  limit: 20,
  total: 0,
};

/**
 * Fetches a paginated, slim appointment list for an event.
 * @async
 * @param {Function} navigate - React Router navigation function
 * @param {string} eventId - The event's unique identifier
 * @param {Object} [params]
 * @returns {Promise<{ items: Array, page: number, limit: number, total: number }>}
 */
export const getEventAppointmentsPage = async (
  navigate,
  eventId,
  params = {},
) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}appointment/event/${eventId}/page-data${buildAppointmentQueryString(params)}`,
      options,
    );
    const result = await response.json();

    if (result?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return emptyAppointmentsPage;
      }
      return await getEventAppointmentsPage(navigate, eventId, params);
    }

    return {
      items: result?.data?.items || [],
      page: result?.data?.page || params.page || 1,
      limit: result?.data?.limit || params.limit || 20,
      total: result?.data?.total || 0,
    };
  } catch (error) {
    logger.error("Error fetching paginated appointments for event:", {
      err: sanitizeError(error),
    });
    return emptyAppointmentsPage;
  }
};

/**
 * Fetches slim scheduled appointments for the event calendar.
 * @async
 * @param {Function} navigate - React Router navigation function
 * @param {string} eventId - The event's unique identifier
 * @param {Object} [params]
 * @returns {Promise<Array>}
 */
export const getEventAppointmentsCalendar = async (
  navigate,
  eventId,
  params = {},
) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}appointment/event/${eventId}/calendar${buildAppointmentQueryString(params)}`,
      options,
    );
    const result = await response.json();

    if (result?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getEventAppointmentsCalendar(navigate, eventId, params);
    }

    return result?.data?.items || [];
  } catch (error) {
    logger.error("Error fetching calendar appointments for event:", {
      err: sanitizeError(error),
    });
    return [];
  }
};

/**
 * Fetches all appointments for a buyer company in a specific event.
 * @async
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @param {string} buyerCompanyId - The buyer company's unique identifier
 * @param {string} eventId - The event's unique identifier
 * @returns {Promise<Array>} Array of appointment objects or empty array on error
 */
export const getBuyerCompanyAppointmentsForEvent = async (
  navigate,
  buyerCompanyId,
  eventId,
) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}appointment/event/${eventId}/buyerCompany/${buyerCompanyId}/`,
      options,
    );
    const result = await response.json();

    logger.info("getBuyerCompanyAppointmentsForEvent fetch result:", {
      data: result,
    });

    if (result?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getBuyerCompanyAppointmentsForEvent(
        navigate,
        buyerCompanyId,
        eventId,
      );
    }
    return result.data;
  } catch (error) {
    logger.error("Error fetching buyer company appointments for event:", {
      err: sanitizeError(error),
    });
    return [];
  }
};

/**
 * Fetches the count of unconfirmed (non-rejected) appointments for a specific event.
 * Uses GET /appointment/event/{eventId}/unconfirmed-count.
 * @async
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @param {string} eventId - The event's unique identifier
 * @returns {Promise<number>} Unconfirmed appointment count, or 0 on error
 */
export const getUnconfirmedAppointmentCountForEvent = async (
  navigate,
  eventId,
) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}appointment/event/${eventId}/unconfirmed-count`,
      options,
    );
    const result = await response.json();

    if (result?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return 0;
      }
      return await getUnconfirmedAppointmentCountForEvent(navigate, eventId);
    }

    return result?.data?.count ?? 0;
  } catch (error) {
    logger.error("Error fetching unconfirmed appointment count for event:", {
      err: sanitizeError(error),
    });
    return 0;
  }
};

/**
 * Creates a virtual meeting for an existing appointment.
 * @async
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @param {string} eventId - The event's unique identifier
 * @param {string} appointmentId - The appointment's unique identifier
 * @param {Object} virtualMeetingData - Virtual meeting configuration data
 * @returns {Promise<Object|null>} Created virtual meeting data or null on error
 */
export const createVirtualAppointment = async (
  navigate,
  eventId,
  appointmentId,
  virtualMeetingData,
) => {
  const options = {
    method: "PUT",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify({ virtualMeetingData }),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}appointment/${eventId}/${appointmentId}/virtual`,
      options,
    );
    const result = await response.json();

    logger.info("createVirtualAppointment fetch result:", { data: result });

    if (result?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await createVirtualAppointment(
        navigate,
        eventId,
        appointmentId,
        virtualMeetingData,
      );
    }

    return result;
  } catch (error) {
    logger.error("Error creating virtual appointment:", {
      err: sanitizeError(error),
    });
    return null;
  }
};

/**
 * Cancels appointments sent by a company to a specific buyer company in an event.
 * @async
 * @param {string} eventId - The event's unique identifier
 * @param {string} companyId - Sender company ID (idEmpresaUsuarioRemitente)
 * @param {string} buyerCompanyId - Buyer company ID (idEmpresaCompradora) for the selected card
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @returns {Promise<boolean>} True if cancellation was successful, false otherwise
 */
export const cancelAppointment = async (
  eventId,
  companyId,
  buyerCompanyId,
  navigate,
) => {
  const options = {
    method: "DELETE",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const query = new URLSearchParams({ buyerCompanyId }).toString();
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}appointment/event/${eventId}/company/${companyId}?${query}`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return false;
      }
      return await cancelAppointment(
        eventId,
        companyId,
        buyerCompanyId,
        navigate,
      );
    }

    return response.ok;
  } catch (error) {
    logger.error("Error deleting banner:", { err: sanitizeError(error) });
    return false;
  }
};
